import React, {
  useContext, useEffect, useMemo, Fragment,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Link, matchPath, useLocation, useNavigate,
} from 'react-router-dom';
import classnames from 'classnames';

import Config from '@skin_config';
import { useTranslation } from '@common/hooks/translation';

import checkRouteConfig from '@app/dev/helpers/checkRouteConfig';
import { TicketsContext } from '@tickets/store/context';
import { PopupContext } from '@app/dev/contexts/popup';
import { POPUPS } from '@common/constants/popups';
import { checkTerminalMode } from '@common/helpers';
import TooltipDropDown from '@common/components/tooltipDropDown';
import Timer from '@common/components/timer';
import { getAllTickets } from '@tickets/helpers/addTicketByType';
import { getAuthData } from '@common/helpers/auth';
import SnowPic from '@app/dev/images/snow.svg';
import SnowHat from '@app/dev/images/snow-hat.svg';
import { DepositModeEnum } from '../../../constants/enums/depositMode';

import Balance from '../../components/balance';
import SettingSection from '../settingsSection';
import LoyalCardItem from '../../components/loyalcard';
import { useBalance } from '../../hooks/balance';
import AdminDropDown from '../adminDropdown';
import DepositButton from './pieces/depositButton';
import { CashoutButton } from './pieces/cashoutButton';
import { VerificationInfo } from './pieces/verificationInfo';
import { VerificationLogout } from './pieces/verificationLogout';
import User from '../user';

import './style.scss';
import ClientDepositButton from './pieces/clientDepositButton';
import { MpesaPayment } from '../../components/mpesaPayment';
import { isFilterActive } from '@app/dev/helpers/headerFilter';
import WorldCupTab from './worldCupTab';

const availableLogoSizes = new Set([
  'small',
  'mid',
  'mid-with-height',
  'big',
  'medium',
  'huge',
  'extra-huge',
  'extra-huge-with-height',
  'ultra-huge',
]);

const Header = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [ticketsState, , resetTicketsState] = useContext(TicketsContext);
  const ticketsList = getAllTickets(ticketsState);
  const {
    isAdminLoggedIn,
    verification,
    startupSettings,
  } = useSelector((state) => state.terminal);
  const terminalBalance = useBalance();

  const [{ openPopup }, activePopups] = useContext(PopupContext);

  const location = useLocation();
  const authData = getAuthData();

  const { skinType } = authData;
  const isHome = location.pathname === '/';
  const isVerificationDone = !startupSettings.IsPlayerVerificationEnabled || verification.isVerified;
  const userLoggedIn = !!user.loggedIn || !!user.loggedInByRFID;
  const showCheckTicketButton = location.pathname !== '/tickets' && !ticketsList.length && !userLoggedIn && !Config.tickets.disableFlow;

  const routes = useMemo(
    () => Config.routes.filter((item) => !item.disableInHeader).filter((item) => {
      const routeConfig = checkRouteConfig(ticketsList.length)[item.url]?.() ?? true;
      const enableMultipleTickets = ticketsList.length && Config.tickets.enableMultipleTickets;

      if (skinType === 'casino') {
        return item.enabled && (routeConfig || enableMultipleTickets);
      }
      if (skinType === 'sport') {
        return (item.url !== 'casino' && item.url !== 'liveCasino') && item.enabled && (routeConfig || enableMultipleTickets);
      }
      if (!skinType && item.disableByDefault) {
        return (item.url !== 'casino' && item.url !== 'liveCasino') && item.enabled && (routeConfig || enableMultipleTickets);
      }

      return item.enabled && (routeConfig || enableMultipleTickets);
    }),
    [ticketsList.length, skinType],
  );

  const showCashoutButton = (!Config.header.disableCashoutAfterLogin || !Config.header.disableCashout)
    && (isVerificationDone || !!terminalBalance)
    && !userLoggedIn
    && !Config.main.isViewMode;

  const customLogoSize = !availableLogoSizes.has(Config.main.logoSize) && Config.main.logoSize.split('x');

  const onNavigate = (item) => {
    if (!user.loggedIn && item.mandatoryLogin) {
      openPopup(POPUPS.LOGIN_FORM, { onSuccessLogin: () => navigate(item.url) });
    } else {
      navigate(item.url);
    }
  };

  const exitWebApp = () => window.vuplex?.postMessage(JSON.stringify({ type: 'exit' }));

  useEffect(() => {
    const matched = routes
      .filter((item) => item.url)
      .find((item) => matchPath(item.url, location.pathname));

    if (matched?.mandatoryLogin && !user.loggedIn) {
      navigate('/');
      openPopup(POPUPS.LOGIN_FORM);
    }
  }, [location.pathname, user.loggedIn]);

  return (
    <div className="header-container-wrapper">
      <div
        className={`header-container ${isHome && Config.main.enableDefaultHomeHeader ? 'header-home' : ''}`}
      >
        <div className="header-items-list">
          <div className="pages" style={{marginLeft: `${Config.main.headerPagesLeftDistance}px`}}>
            {Config.main.hasLogo && (
              <div className="logo-wrapper">
                <Link
                  to="/"
                  className={`logo ${availableLogoSizes.has(Config.main.logoSize) ? Config.main.logoSize : ''}`}
                  style={customLogoSize ? {
                    width: +customLogoSize[0],
                    height: +customLogoSize[1],
                  } : {}}
                />
                {
                  Config.main.enableWinterMode && <img src={SnowHat} alt="snow-hat" className="snow-hat" />
                }
              </div>
            )}

            {routes?.map((item) => {
              const validSubItems = Config[item.subItemsKey]?.filter((item) => item.enabled);
              const nestedUrls = item.nestedItems?.map((subItem) => subItem.url).filter(Boolean) || [];
              // const isActive = isFilterActive(location.pathname, item.url, 'filterType', 'todaytoday')
              const isActive = matchPath({ path: (item.url || ''), end: false }, location.pathname)
                || nestedUrls?.some((url) => matchPath({ path: url, end: false }, location.pathname))
                || Config[item.subItemsKey]?.some((subItem) => subItem.url === location.pathname);

              return (
                <Fragment key={item.url ? `url-${item.url}` : `sub-items-${item.subItemsKey}`}>
                  {validSubItems?.length ? (
                    <TooltipDropDown
                      header={t(item.name)}
                      options={Config[item.subItemsKey].map((option) => ({
                        ...option,
                        buttonName: option.name,
                        callback: () => navigate(option.url),
                      }))}
                      className={classnames('header-item sub-items', {
                        active: Config[item.subItemsKey].some((item) => item.url === location.pathname),
                      })}
                    />
                  ) : item.outSideLink
                    ? (
                      <a
                        className="header-item"
                        href="https://www.zaxbet.com/en/promotions/all"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('Promotions')}
                      </a>
                    )
                    : (
                      <div
                        key={item.url}
                        onClick={() => onNavigate(item)}
                        className={`header-item ${item.url} ${isActive ? 'active' : ''}`}
                      >
                        <span>{t(item.name)}</span>
                        {item.showTicketsExpireTimer
                          && !matchPath(item.url, location.pathname)
                          && !activePopups.some((popup) => popup.type === POPUPS.TICKET_DETAILS_POPUP) && (
                            <Timer variant="block" duration={60} onFinish={resetTicketsState} />
                          )}
                      </div>
                    )}
                  {(item.hasNewLabel || validSubItems?.some((item) => item.hasNewLabel))
                    && <span className="new-label">{t('New')}</span>}
                </Fragment>
              );
            })}

            {Config.main.enableWorldCup && <WorldCupTab />}
          </div>
          <div className="other-item-holder">
            {
              Config.main.showExitButton &&
              <div className='exit-button' onClick={exitWebApp}>
                Exit
              </div>
            }
            <div className="other-items">
              {showCheckTicketButton && (
                <button type="button" className="check-ticket-btn" onClick={() => openPopup(POPUPS.CHECK_TICKET_POPUP)}>
                  {t('Check ticket')}
                </button>
              )}

              {startupSettings.IsPlayerVerificationEnabled && <VerificationInfo />}

              {Config.main.enabel18PlusIcon && <div className="plus18" />}
              {verification.isVerified && <VerificationLogout />}
              {(!Config.header.disableAnonymousBalance || user.loggedIn)
                && (!Config.main.isViewMode || user.loggedIn)
                && checkTerminalMode(Config, 'betting') && <Balance />}

              {(Config.main.isWithdrawal && userLoggedIn)
                ? (
                  <div className="cashout" onClick={() => openPopup(POPUPS.WITHDRAWAL)}>
                    <i className="global-icon icon-cashout" />
                    <p>{t('Withdrawal')}</p>
                  </div>
                ) : (showCashoutButton) && (
                  <CashoutButton />
                  // <div className="cashout" onClick={openCashoutPopup}>
                  //   <i className="global-icon icon-cashout" />
                  //   <p>{t('Cash out')}</p>
                  // </div>
                )}

              {
                (userLoggedIn && !!Config.cashoutMethods.length) && (
                  <div className="cashout" onClick={() => openPopup(POPUPS.CASHOUT_METHODS)}>
                    <i className="global-icon icon-cashout" />
                    <p>{t('Withdrawal')}</p>
                  </div>
                )
              }
              {
                Config.registrationFormConfigs.enabledInHeader && (
                  <div className="sign-in" onClick={() => openPopup(POPUPS.REGISTRATION_FORM)}>
                    {/* <i className="global-icon icon-cashout" /> */}
                    <p>{t('Registration')}</p>
                  </div>
                )
              }

              {Config.main.enableMpesaPayment && <MpesaPayment />}

              <User />

              <ClientDepositButton />

              <DepositButton />

              {isVerificationDone && <LoyalCardItem />}

              {/* <span className="global-icon icon-arrow-down5"/> */}
              {/* {Config.main.enableLanguageSwitcher && <LanguageSwitcher />} */}
              <SettingSection />

              {!!isAdminLoggedIn && Config.main.enableHeaderAdminSection && <AdminDropDown />}
              {/* <AdminDropDown /> */}
              {Config.main.termsAndConditions && (
                <div
                  className="term-and-conditions"
                  onClick={() => openPopup(POPUPS.TERMS_AND_CONDITIONS)}
                >
                  <i className="global-icon icon-help1 help-icon" />
                </div>
              )}

              {/* {Config.main.enableThemeToggle && <ThemeToggle />} */}
            </div>
          </div>
        </div>
      </div>
      {Config.main.enableWinterMode && <img src={SnowPic} alt="snow" className="snow-pic" />}
    </div>
  );
};

export default Header;
